import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import MainFooter from '@/components/MainFooter';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { issues } from '@/data/newsletter-issues';
import { Helmet } from 'react-helmet-async';

const NewsletterDetail = () => {
  const { id } = useParams();
  const [issue, setIssue] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    // Find the issue by ID
    const foundIssue = issues.find(issue => issue.id === id);
    setIssue(foundIssue);
    setLoading(false);
  }, [id]);

  // Pre-connect to the iframes domains to speed up loading
  useEffect(() => {
    if (issue?.url) {
      try {
        const url = new URL(issue.url);
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = `${url.protocol}//${url.hostname}`;
        document.head.appendChild(link);
        
        return () => {
          document.head.removeChild(link);
        };
      } catch (e) {
        console.error('Error parsing URL for preconnect:', e);
      }
    }
  }, [issue]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="flex-grow flex flex-col items-center justify-center p-8">
          <h1 className="text-3xl font-bold mb-4">Newsletter not found</h1>
          <p className="mb-8">The newsletter edition you're looking for doesn't exist.</p>
          <Link to="/talent-tales-newsletter-cutshort#past-issues">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Past Issues
            </Button>
          </Link>
        </main>
        <MainFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{issue.title} | The Talent Tales by Cutshort</title>
        <meta name="description" content={`Read the full newsletter edition: ${issue.title}. Get insights on tech recruitment and career trends.`} />
        <meta property="og:title" content={`${issue.title} | The Talent Tales by Cutshort`} />
        <meta property="og:description" content={`Read the full newsletter edition: ${issue.title}. Get insights on tech recruitment and career trends.`} />
        <meta property="og:image" content="/lovable-uploads/63ba4f2f-b684-458f-9cc2-964a2619d838.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@cutshortHQ" />
        <meta name="twitter:image" content="/lovable-uploads/63ba4f2f-b684-458f-9cc2-964a2619d838.png" />
        <link rel="preconnect" href={new URL(issue.url).origin} />
      </Helmet>

      <NavBar />
      <main className="flex-grow container px-4 md:px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <Link to="/talent-tales-newsletter-cutshort#past-issues" className="inline-flex items-center text-primary hover:text-primary-light mb-6 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Past Issues
          </Link>
          
          <div className="mb-8">
            <p className="text-sm text-secondary mb-2">{new Date(issue.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <h1 className="text-3xl md:text-4xl font-bold mb-6">{issue.title}</h1>
            
            {issue.image && (
              <div className="w-full h-64 md:h-80 overflow-hidden rounded-lg mb-8">
                <img 
                  src={issue.image} 
                  alt={issue.title}
                  className="w-full h-full object-cover"
                  width="800"
                  height="400"
                  loading="eager" // Changed from priority="true" to loading="eager"
                />
              </div>
            )}
          </div>

          <div className="newsletter-content">
            {!iframeLoaded && (
              <div className="w-full h-[200px] flex items-center justify-center bg-neutral-light/30 rounded-lg mb-4">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
              </div>
            )}
            <iframe 
              src={issue.url} 
              title={`Talent Tales - ${issue.date}`}
              className={`w-full h-[800px] border-none ${iframeLoaded ? 'block' : 'h-0'}`}
              loading="lazy"
              onLoad={() => setIframeLoaded(true)}
            />
          </div>
          
          <div className="mt-12 pt-8 border-t border-neutral-light">
            <h2 className="text-2xl font-bold mb-4">Subscribe to Talent Tales</h2>
            <p className="mb-6">Get the latest insights on tech recruitment and career trends delivered to your inbox.</p>
            <a href="https://cutshort.io/a/newsletter-hs-form" target="_blank" rel="noopener noreferrer">
              <Button>Subscribe Now</Button>
            </a>
          </div>
        </div>
      </main>
      <MainFooter />
    </div>
  );
};

export default NewsletterDetail;


